<template>
  <div>
    <div class="grey lighten-5 fill-width fill-height py-3 px-4">
      <div class="d-flex align-center">
        <researcher-avatar-cluster
          class="mr-2"
          :researchers="researchers"
        />
        <span v-if="preScreened" class="text-body-2">
          This reference was pre-screened as
          <v-icon small :color="screeningOutcomeProps.iconColor">
            {{ screeningOutcomeProps.icon }}
          </v-icon>
          <span class="font-weight-bold">
            {{ screeningOutcomeProps.text }}
          </span>
          when uploaded.
        </span>
        <span v-else class="text-body-2">
          <researcher-inline-list :researchers="researchers" />
          {{ fullTextReview ? 'reviewed' : 'voted' }}
        </span>
      </div>
      <!-- Sits on its own line below the reviewer names: the bar renders in
           narrow side panels where an inline link gets cramped. -->
      <vote-log-popup
        v-if="!preScreened"
        class="d-inline-block mt-1"
        :reference="reference"
        :full-text-review="fullTextReview"
      >
        <a class="text-body-2">{{
          fullTextReview ? 'Show reviews' : 'Show votes'
        }}</a>
      </vote-log-popup>
    </div>
  </div>
</template>

<script>
import ResearcherAvatarCluster from '@/components/Common/ResearcherAvatarCluster';
import ResearcherInlineList from '@/components/Common/ResearcherInlineList';
import VoteLogPopup from '@/components/References/VoteLogPopup';
import { Reference } from '@/models';
import {
  RelevanceAssessment,
  ScreeningOutcomeResolution,
  InclusionAssessment,
} from '@/enums';

const OUTCOME_TO_PROPS = {
  [RelevanceAssessment.RELEVANT]: {
    icon: 'thumb_up_alt',
    iconColor: 'success',
    text: 'Relevant',
  },
  [RelevanceAssessment.NOT_RELEVANT]: {
    icon: 'thumb_down_alt',
    iconColor: 'error',
    text: 'Not Relevant',
  },
  [RelevanceAssessment.NOT_SURE]: {
    icon: 'help',
    iconColor: 'warning',
    text: 'Not Sure',
  },
  [InclusionAssessment.INCLUDE]: {
    icon: 'done',
    iconColor: 'success',
    text: 'Include',
  },
  [InclusionAssessment.EXCLUDE]: {
    icon: 'close',
    iconColor: 'error',
    text: 'Exclude',
  },
};

export default {
  name: 'VoteHistoryBar',
  components: {
    ResearcherAvatarCluster,
    ResearcherInlineList,
    VoteLogPopup,
  },
  props: {
    reference: {
      type: Reference,
      required: true,
    },
    fullTextReview: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    researchers() {
      const votes = this.fullTextReview
        ? this.reference.fullTextReviewVotes
        : this.reference.reviewerScreeningVotes;
      return votes
        .map((vote) => vote.reviewer?.researcher)
        .filter(Boolean);
    },
    outcome() {
      return this.fullTextReview
        ? this.reference.fullTextReviewOutcome
        : this.reference.screeningOutcome;
    },
    preScreened() {
      return (
        this.outcome.resolution ===
        ScreeningOutcomeResolution.PRE_SCREENED
      );
    },
    screeningOutcome() {
      return this.outcome.outcome;
    },
    screeningOutcomeProps() {
      return OUTCOME_TO_PROPS[this.screeningOutcome];
    },
  },
};
</script>

<style lang="scss" scoped>
.fill-width {
  width: 100%;
}
</style>
